import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  FolderKanban,
  Loader2,
  Package,
  Pin,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { TagBadge } from "@/components/ui/TagBadge";
import { IdeasMultiSelect } from "./IdeasMultiSelect";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useCreateDecision,
  useDecisionCategories,
  useDecisionImpacts,
  useDecisionStatuses,
  useDecisionTags,
} from "../hooks/useDecisions";

const BORDER = "hsl(210,30%,85%)";

export type DecisionPrefill = {
  companyId?: string | null;
  productId?: string | null;
  projectId?: string | null;
  meetingId?: string | null;
  meetingTitle?: string | null;
  taskId?: string | null;
  actionId?: string | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
  prefill?: DecisionPrefill;
  companyId?: string;
  productId?: string;
  projectId?: string;
  meetingId?: string;
  taskId?: string;
  actionId?: string;
};

export function NewDecisionDrawer({
  isOpen,
  onClose,
  onCreated,
  prefill,
  companyId: presetCompanyId,
  productId: presetProductId,
  projectId: presetProjectId,
  meetingId: presetMeetingId,
}: Props) {
  const createDecision = useCreateDecision();
  const navigate = useNavigate();
  const categories = useDecisionCategories().data ?? [];
  const impacts = useDecisionImpacts().data ?? [];
  const statuses = useDecisionStatuses().data ?? [];
  const tagOptions = useDecisionTags().data ?? [];

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState<string>("");
  const [impact, setImpact] = useState<string>("medium");
  const [status, setStatus] = useState<string>("open");
  const [visibility, setVisibility] = useState<"team" | "private">("team");
  const [tags, setTags] = useState<string[]>([]);
  const [isPinned, setIsPinned] = useState(false);
  const [decidedById, setDecidedById] = useState("");
  const [decidedAt, setDecidedAt] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [companyId, setCompanyId] = useState(
    prefill?.companyId ?? presetCompanyId ?? "",
  );
  const [productId, setProductId] = useState(
    prefill?.productId ?? presetProductId ?? "",
  );
  const [projectId, setProjectId] = useState(
    prefill?.projectId ?? presetProjectId ?? "",
  );
  const [meetingId, setMeetingId] = useState(
    prefill?.meetingId ?? presetMeetingId ?? "",
  );
  const [ideaIds, setIdeaIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setContent("");
      setCategory("");
      setImpact("medium");
      setStatus("open");
      setVisibility("team");
      setTags([]);
      setIsPinned(false);
      setDecidedById("");
      setDecidedAt("");
      setReviewDate("");
      setCompanyId(prefill?.companyId ?? presetCompanyId ?? "");
      setProductId(prefill?.productId ?? presetProductId ?? "");
      setProjectId(prefill?.projectId ?? presetProjectId ?? "");
      setMeetingId(prefill?.meetingId ?? presetMeetingId ?? "");
      setIdeaIds([]);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: companyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: companyId || undefined,
    productId: productId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: meetingsResult } = useMeetingList({
    page: 1,
    limit: 100,
    projectId: projectId || undefined,
  });
  const meetings = meetingsResult?.data ?? [];

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const users = usersResult?.data ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setError(null);
    try {
      const res = await createDecision.mutateAsync({
        title: title.trim(),
        content,
        status,
        category: category || null,
        impact,
        tags,
        visibility,
        isPinned,
        decidedAt: decidedAt ? new Date(decidedAt).toISOString() : null,
        reviewDate: reviewDate ? new Date(reviewDate).toISOString() : null,
        companyId: companyId || null,
        productId: productId || null,
        projectId: projectId || null,
        meetingId: meetingId || null,
        decidedById: decidedById || null,
        ideaIds,
      });
      onCreated?.(res.decision.code ?? res.decision.id);
      onClose();
      navigate(`/decisions/${res.decision.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create decision");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: BORDER }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: BORDER }}
        >
          <h2 className="text-lg font-semibold text-foreground">New Decision</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="decision-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="decision-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter decision title"
                required
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsPinned((v) => !v)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors"
                style={{
                  borderColor: isPinned
                    ? "hsl(40,95%,60%)"
                    : BORDER,
                  background: isPinned ? "hsl(40,95%,95%)" : "white",
                  color: isPinned ? "hsl(40,95%,30%)" : "hsl(215,16%,47%)",
                }}
              >
                <Pin
                  className="h-[14px] w-[14px]"
                  strokeWidth={1.5}
                  style={{
                    fill: isPinned ? "hsl(40,95%,55%)" : "none",
                  }}
                />
                {isPinned ? "Pinned" : "Pin this decision"}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="decision-category">Category</Label>
                <select
                  id="decision-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">No category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="decision-impact">Impact</Label>
                <select
                  id="decision-impact"
                  value={impact}
                  onChange={(e) => setImpact(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {impacts.length === 0 ? (
                    <>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </>
                  ) : (
                    impacts.map((i) => (
                      <option key={i.id} value={i.value}>
                        {i.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="decision-status">Status</Label>
                <select
                  id="decision-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {statuses.length === 0 ? (
                    <>
                      <option value="open">Open</option>
                      <option value="decided">Decided</option>
                      <option value="implemented">Implemented</option>
                      <option value="archived">Archived</option>
                    </>
                  ) : (
                    statuses.map((s) => (
                      <option key={s.id} value={s.value}>
                        {s.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="decision-decided-by">Decided By</Label>
                <select
                  id="decision-decided-by"
                  value={decidedById}
                  onChange={(e) => setDecidedById(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="decision-decided-at">Decided At</Label>
                <Input
                  id="decision-decided-at"
                  type="date"
                  value={decidedAt}
                  onChange={(e) => setDecidedAt(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="decision-review-date">Review Date</Label>
                <Input
                  id="decision-review-date"
                  type="date"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1.5">
                {tagOptions.map((tagOpt) => {
                  const selected = tags.includes(tagOpt.value);
                  return (
                    <button
                      key={tagOpt.id}
                      type="button"
                      onClick={() =>
                        setTags((prev) =>
                          prev.includes(tagOpt.value)
                            ? prev.filter((x) => x !== tagOpt.value)
                            : [...prev, tagOpt.value],
                        )
                      }
                      style={{
                        opacity: selected ? 1 : 0.55,
                        filter: selected ? "none" : "grayscale(0.3)",
                      }}
                    >
                      <TagBadge
                        value={tagOpt.value}
                        label={tagOpt.label}
                        size="md"
                      />
                    </button>
                  );
                })}
                {tagOptions.length === 0 && (
                  <span className="text-xs text-muted-foreground">
                    No tags configured yet. Add them in Dropdown Manager.
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Visibility</Label>
              <div
                className="inline-flex rounded-md border p-0.5"
                style={{ borderColor: BORDER }}
              >
                {(["team", "private"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVisibility(v)}
                    className="px-3 py-1 text-xs font-medium rounded transition-colors"
                    style={{
                      background:
                        visibility === v ? "hsl(210,55%,88%)" : "transparent",
                      color:
                        visibility === v
                          ? "hsl(210,100%,25%)"
                          : "hsl(215,16%,47%)",
                    }}
                  >
                    {v === "team" ? "Team" : "Private"}
                  </button>
                ))}
              </div>
            </div>

            <div
              className="space-y-3 rounded-lg border p-4"
              style={{ borderColor: "hsl(210,30%,90%)" }}
            >
              <div className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                Association
              </div>

              <div className="space-y-2">
                <Label htmlFor="decision-company">Company</Label>
                <div className="relative">
                  <Building2
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    id="decision-company"
                    value={companyId}
                    onChange={(e) => {
                      setCompanyId(e.target.value);
                      setProductId("");
                      setProjectId("");
                    }}
                    className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">No company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="decision-product">Product</Label>
                <div className="relative">
                  <Package
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    id="decision-product"
                    value={productId}
                    onChange={(e) => {
                      setProductId(e.target.value);
                      setProjectId("");
                    }}
                    className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">No product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="decision-project">Project</Label>
                <div className="relative">
                  <FolderKanban
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    id="decision-project"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">No project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="decision-meeting">Meeting</Label>
                <div className="relative">
                  <CalendarDays
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    id="decision-meeting"
                    value={meetingId}
                    onChange={(e) => setMeetingId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">No meeting</option>
                    {meetings.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Linked Ideas</Label>
                <IdeasMultiSelect
                  selectedIds={ideaIds}
                  onChange={setIdeaIds}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Content</Label>
              <RichTextEditor
                content={content}
                onChange={setContent}
                toolbarVariant="full"
                minHeight={200}
                placeholder="Describe the decision..."
              />
            </div>

            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: BORDER }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || createDecision.isPending}
            >
              {createDecision.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Decision
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
